import { mapArrayResponse } from "src/utils/api";
import { createSelectableObject, SelectableObject } from "src/components/Form/types";
import type {
  AircraftTypeShort,
  AircraftTypesResponse,
  UserAircraftTypesShort,
  CrewTypeUserIdParam,
  CrewTypeOperationUserIdParam,
  UserAircraftOperationRecordsResponse,
  UserAircraftOperationHistoryEntry,
  UserAircraftOperationHistoryResponse,
  OperationHistoryQuery,
  UpsertOperationBody,
  HistoryIdParam,
  PatchHistoryCommentBody,
} from "src/shared/api-types/aircraft";
import type { SimpleResponse, CrewIdParam, CrewUserIdParam } from "src/shared/api-types/common";

import { typedRequest } from "./typedRequest";

export type AircraftType = AircraftTypeShort;

export async function getCrewAircraftTypes(params: CrewIdParam): Promise<AircraftTypesResponse>;
export async function getCrewAircraftTypes(params: CrewUserIdParam): Promise<AircraftTypesResponse>;
export async function getCrewAircraftTypes(params: CrewIdParam | CrewUserIdParam): Promise<AircraftTypesResponse> {
  const hasUserId = "userId" in params;
  const url = hasUserId ? "/aircraft/crew/:crewId/user/:userId" : "/aircraft/crew/:crewId";
  return typedRequest.get({ url, params });
}

export const getCrewUsersAircraftTypes = async (params: CrewIdParam): Promise<UserAircraftTypesShort> => {
  return typedRequest.get({ url: "/aircraft/crew/:crewId/user", params });
};

export async function getCrewAircraftTypesAsSelectable(params: CrewIdParam): Promise<SelectableObject[]>;
export async function getCrewAircraftTypesAsSelectable(params: CrewUserIdParam): Promise<SelectableObject[]>;
export async function getCrewAircraftTypesAsSelectable(
  params: CrewIdParam | CrewUserIdParam,
): Promise<SelectableObject[]> {
  const response = await getCrewAircraftTypes(params);
  return mapArrayResponse(response, (aircraft) => createSelectableObject(aircraft.typeId, aircraft.typeName));
}

export const assignAircraftToUser = async (params: CrewTypeUserIdParam): Promise<SimpleResponse> => {
  return typedRequest.post({ url: "/aircraft/crew/:crewId/user/:userId/aircraft/type/:typeId", params });
};

export const unassignAircraftFromUser = async (params: CrewTypeUserIdParam): Promise<SimpleResponse> => {
  return typedRequest.delete({ url: "/aircraft/crew/:crewId/user/:userId/aircraft/type/:typeId", params });
};

export const getAircraftOperationRecords = async (
  params: CrewUserIdParam,
): Promise<UserAircraftOperationRecordsResponse> => {
  return typedRequest.get({ url: "/aircraft/crew/:crewId/user/:userId/operations", params });
};

export const upsertAircraftOperationRecord = async (
  params: CrewTypeOperationUserIdParam,
  body: UpsertOperationBody,
): Promise<UserAircraftOperationHistoryEntry> => {
  return typedRequest.put({
    url: "/aircraft/crew/:crewId/user/:userId/type/:typeId/operation/:operationId",
    params,
    body,
  });
};

export const getAircraftOperationHistory = async (
  params: CrewTypeOperationUserIdParam,
  query?: OperationHistoryQuery,
): Promise<UserAircraftOperationHistoryResponse> => {
  const url = query?.includeHidden
    ? "/aircraft/crew/:crewId/user/:userId/type/:typeId/operation/:operationId/history?includeHidden=true"
    : "/aircraft/crew/:crewId/user/:userId/type/:typeId/operation/:operationId/history";

  return typedRequest.get({
    url,
    params,
  });
};

export const patchHistoryComment = async (
  params: HistoryIdParam,
  body: PatchHistoryCommentBody,
): Promise<SimpleResponse> => {
  return typedRequest.patch({ url: "/aircraft/crew/:crewId/history/:historyId/comment", params, body });
};

export const hideAircraftOperationHistoryEntry = async (params: HistoryIdParam): Promise<SimpleResponse> => {
  return typedRequest.delete({ url: "/aircraft/crew/:crewId/history/:historyId", params });
};
